import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

export default function PurchaseProofViewer({ url, onClose }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const closeButton = useRef(null);

  useEffect(() => {
    let objectUrl;
    setPreview(null);
    setError('');
    try {
      let src = url;
      let isPdf = /\.pdf(?:[?#]|$)/i.test(url);
      // Uploaded receipts are stored as data URLs. Use a blob URL so PDFs can
      // also be opened/downloaded without navigating a tab to a data URL.
      if (url.startsWith('data:')) {
        const match = /^data:([^;,]+);base64,([\s\S]*)$/.exec(url);
        if (!match || !/^(image\/[^;]+|application\/pdf)$/i.test(match[1])) throw new Error('Unsupported proof');
        const bytes = Uint8Array.from(atob(match[2]), char => char.charCodeAt(0));
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: match[1] }));
        src = objectUrl;
        isPdf = match[1].toLowerCase() === 'application/pdf';
      }
      setPreview({ src, isPdf });
    } catch {
      setError('Bukti pembelian tidak dapat dibuka. File mungkin rusak atau formatnya tidak didukung.');
    }
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeButton.current?.focus();
    const onKeyDown = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="purchase-proof-title"
        className="bg-white rounded-2xl p-4 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={event => event.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 id="purchase-proof-title" className="font-bold text-isk-text">Bukti Pembelian</h3>
          <button ref={closeButton} type="button" onClick={onClose} aria-label="Tutup bukti pembelian" className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        {error && <p role="alert" className="text-sm text-red-600 mb-3">{error}</p>}
        {preview && (
          <>
            {!error && (preview.isPdf ? (
              <iframe title="Bukti pembelian PDF" src={preview.src} className="w-full h-[65vh] border rounded-lg" />
            ) : (
              <img src={preview.src} alt="Bukti pembelian" className="mx-auto max-w-full max-h-[70vh] object-contain rounded-lg"
                onError={() => setError('Gambar bukti pembelian gagal dimuat. Berkas mungkin tidak tersedia atau formatnya tidak didukung.')} />
            ))}
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-isk-teal">
              {preview.isPdf && <a href={preview.src} target="_blank" rel="noreferrer" className="underline">Buka PDF di tab baru</a>}
              <a href={preview.src} download className="underline">Unduh bukti</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
